/**
 * SensitivityAnalysis — what-if explorer. Slide each criterion's weight from
 * 0–100 and watch the ranking shift in real time. No server round-trip per
 * tick; recompute is purely client-side via the E1 frontend mirror of
 * scoringService.computeWeightedScores.
 *
 * Data sources:
 *   - criteria      from `state.scoring.criteriaByEvaluation[evalId]`
 *   - baseline ranking + per-(site, criterion) avgScores
 *                   from `state.scoring.summaryByEvaluation[evalId]`
 *
 * For the recompute we synthesise a `scores` array from the summary's avg
 * scores — one synthetic record per (site, criterion) pair carrying the
 * pre-computed average. The service averages duplicates, so a single
 * synthetic score equals the avg, and the only thing changing across slider
 * ticks is the criteria weights.
 *
 * Save: parallel `updateCriterion` calls, then `fetchScoringSummary` so the
 * baseline updates everywhere (workspace ranking, charts, etc.).
 *
 * Linear: MYD-209 (E4). Supersedes the cancelled MYD-138.
 */

import { useCallback, useMemo, useRef, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { toast } from 'components/ui/toast-store'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogDescription,
	DialogFooter,
} from 'components/ui/dialog'
import { Button } from 'components/ui/button'
import { Badge } from 'components/ui/badge'
import {
	RotateCcw,
	Save,
	ArrowUp,
	ArrowDown,
	Minus,
	Loader2,
	AlertCircle,
	Activity,
} from 'lucide-react'
import { cn } from 'lib/utils'

import { computeWeightedScores } from 'services/evaluations/scoringService'
import { updateCriterion, fetchScoringSummary } from 'slices/scoringSlice'

interface SensitivityAnalysisProps {
	open: boolean
	onClose: () => void
	evalId: string
}

interface BaselineRow {
	siteId: string
	siteName: string
	rank: number
	overallScore: number | null
}

const SensitivityAnalysis = ({ open, onClose, evalId }: SensitivityAnalysisProps) => {
	const dispatch = useDispatch()

	// Select the STORED array by reference, then sort in a memo. Returning
	// `.slice().sort()` directly out of useSelector hands back a brand-new array
	// on every render, which made the hydration effect below re-run after every
	// render and reset the sliders to their baseline mid-drag.
	const storedCriteria = useSelector(
		(state: any) => state.scoring?.criteriaByEvaluation?.[evalId],
	)
	const criteria = useMemo(
		() =>
			(storedCriteria || [])
				.slice()
				.sort((a: any, b: any) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0)),
		[storedCriteria],
	)
	const summary = useSelector(
		(state: any) => state.scoring?.summaryByEvaluation?.[evalId],
	)

	// Tweakable weights map: criterionId → weight. Hydrated from criteria when
	// the dialog opens; reset when the user clicks Reset.
	const [tweaked, setTweaked] = useState<Record<string, number>>({})
	const [saving, setSaving] = useState(false)
	const [saveError, setSaveError] = useState<string | null>(null)

	// Read criteria through a ref so hydration keys on the open transition only.
	// Keeping `criteria` in the dep list would re-seed the sliders any time the
	// criteria list is refetched while the dialog is open, wiping the user's
	// in-progress what-if.
	const criteriaRef = useRef(criteria)
	criteriaRef.current = criteria

	useEffect(() => {
		if (!open) return
		const next: Record<string, number> = {}
		for (const c of criteriaRef.current) {
			next[c._id] = Number.isFinite(c.weight) ? c.weight : 0
		}
		setTweaked(next)
		setSaveError(null)
	}, [open])

	// Pre-extract baseline ranking for arrow indicators. Shape mirrors
	// scoringService's RankingEntry.
	const baseline: BaselineRow[] = useMemo(() => {
		const rows = summary?.sites ?? []
		const sorted = rows
			.slice()
			.sort((a: any, b: any) => {
				const aS = a.overallScore ?? -Infinity
				const bS = b.overallScore ?? -Infinity
				if (aS !== bS) return bS - aS
				return String(a.siteName ?? '').localeCompare(String(b.siteName ?? ''))
			})
			.map((s: any, idx: number) => ({
				siteId: String(s.siteId),
				siteName: s.siteName,
				rank: idx + 1,
				overallScore: s.overallScore,
			}))
		return sorted
	}, [summary])

	// Live recompute from synthetic scores + tweaked weights. useMemo keys on
	// the tweaked map + summary so slider ticks are perceptually instant.
	const liveRanking = useMemo(() => {
		if (!summary?.sites?.length || criteria.length === 0) return []

		// Synthesise sites + scores from the summary so we can feed the same
		// service the workspace uses. Each (site, criterion) avgScore becomes a
		// single synthetic score record.
		const sites = summary.sites.map((s: any) => ({
			_id: s.siteId,
			name: s.siteName,
		}))
		const scores: Array<{ site: string; criterion: string; value: number }> = []
		for (const s of summary.sites) {
			for (const c of s.criteria ?? []) {
				if (c.avgScore != null) {
					scores.push({
						site: String(s.siteId),
						criterion: String(c.criterionId),
						value: Number(c.avgScore),
					})
				}
			}
		}
		const tweakedCriteria = criteria.map((c: any) => ({
			_id: c._id,
			name: c.name,
			weight: tweaked[c._id] ?? 0,
			category: c.category ?? null,
			sortOrder: c.sortOrder ?? 0,
		}))

		const result = computeWeightedScores({
			criteria: tweakedCriteria,
			sites,
			scores,
		})
		return result.ranking
	}, [criteria, summary, tweaked])

	// Map siteId → baseline rank for change indicators.
	const baselineRankById = useMemo(() => {
		const m: Record<string, number> = {}
		for (const row of baseline) m[row.siteId] = row.rank
		return m
	}, [baseline])

	const totalWeight = useMemo(
		() => Object.values(tweaked).reduce((sum, w) => sum + (Number(w) || 0), 0),
		[tweaked],
	)

	const dirty = useMemo(() => {
		// Compare each tweaked weight to the original criterion weight.
		for (const c of criteria) {
			if ((tweaked[c._id] ?? 0) !== (c.weight ?? 0)) return true
		}
		return false
	}, [criteria, tweaked])

	const handleSliderChange = useCallback((criterionId: string, value: number) => {
		setTweaked((prev) => ({ ...prev, [criterionId]: value }))
	}, [])

	const handleReset = useCallback(() => {
		const next: Record<string, number> = {}
		for (const c of criteria) {
			next[c._id] = Number.isFinite(c.weight) ? c.weight : 0
		}
		setTweaked(next)
		setSaveError(null)
	}, [criteria])

	const handleSave = useCallback(async () => {
		if (!dirty) return
		const ok = await toast.confirm({
			title: 'Save weights',
			message:
				'Save these weights as the new baseline? The ranking and charts on the scoring tab will refresh.',
			confirmLabel: 'Save',
			variant: 'default',
		})
		if (!ok) return
		setSaving(true)
		setSaveError(null)
		try {
			const dispatches = criteria
				.filter((c: any) => (tweaked[c._id] ?? 0) !== (c.weight ?? 0))
				.map((c: any) =>
					// @ts-ignore — thunks
					dispatch(
						updateCriterion({
							evalId,
							criterionId: c._id,
							payload: { weight: tweaked[c._id] ?? 0 },
						}),
					),
				)
			const results = await Promise.all(dispatches)
			const failed = results.find((r: any) => r?.error)
			if (failed) {
				setSaveError(failed.payload || 'Failed to save one or more weights')
				return
			}
			// Refresh the summary so the workspace, charts, and baseline all
			// reflect the new weights.
			// @ts-ignore — thunks
			await dispatch(fetchScoringSummary(evalId))
			onClose()
		} finally {
			setSaving(false)
		}
	}, [criteria, dispatch, evalId, dirty, tweaked, onClose])

	if (!open) return null

	const hasScores = baseline.some((r) => r.overallScore != null)

	return (
		<Dialog open={open} onClose={onClose}>
			<DialogContent className="max-w-3xl flex flex-col max-h-[90vh]">
				<DialogHeader>
					<div className="min-w-0">
						<DialogTitle className="flex items-center gap-2">
							<Activity className="w-5 h-5 text-[#0080fe]" />
							Sensitivity analysis
						</DialogTitle>
						<DialogDescription>
							Slide each criterion's weight from 0–100 to see how the ranking shifts.
							Changes don't save until you click <strong>Save these weights</strong>.
						</DialogDescription>
					</div>
				</DialogHeader>

				<div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-5">
					{!hasScores ? (
						<div className="flex items-start gap-2 px-3 py-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
							<AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
							<span>
								No scores yet. Score at least one site against the criteria before exploring
								sensitivity — without scores there's nothing to re-rank.
							</span>
						</div>
					) : criteria.length === 0 ? (
						<div className="flex items-start gap-2 px-3 py-3 rounded-md bg-amber-50 border border-amber-200 text-amber-800 text-sm">
							<AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
							<span>No criteria configured yet.</span>
						</div>
					) : (
						<>
							{/* Sliders */}
							<div>
								<div className="flex items-center justify-between mb-2">
									<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
										Criterion weights
									</h4>
									<Badge
										className={cn(
											'text-[10px]',
											totalWeight === 100
												? 'bg-emerald-100 text-emerald-700'
												: totalWeight > 100
													? 'bg-red-100 text-red-700'
													: 'bg-amber-100 text-amber-700',
										)}
									>
										Total: {totalWeight}%
									</Badge>
								</div>
								<div className="space-y-3">
									{criteria.map((c: any) => {
										const tweakedVal = tweaked[c._id] ?? 0
										const baselineVal = c.weight ?? 0
										const delta = tweakedVal - baselineVal
										return (
											<div
												key={c._id}
												className="flex items-center gap-3 px-3 py-2 rounded-md border border-gray-200"
											>
												<div className="flex-1 min-w-0">
													<div className="flex items-center justify-between mb-1.5 gap-2">
														<span className="text-sm font-medium text-gray-900 truncate">
															{c.name}
														</span>
														<div className="flex items-center gap-2 shrink-0">
															<span className="text-xs text-gray-400 tabular-nums">
																{baselineVal}% →
															</span>
															<span
																className={cn(
																	'text-sm font-semibold tabular-nums w-12 text-right',
																	delta > 0
																		? 'text-emerald-700'
																		: delta < 0
																			? 'text-red-700'
																			: 'text-gray-700',
																)}
															>
																{tweakedVal}%
															</span>
														</div>
													</div>
													<input
														type="range"
														min={0}
														max={100}
														step={1}
														value={tweakedVal}
														onChange={(e) =>
															handleSliderChange(c._id, Number(e.target.value))
														}
														aria-label={`${c.name} weight`}
														className="w-full accent-[#0080fe]"
													/>
												</div>
											</div>
										)
									})}
								</div>
							</div>

							{/* Live ranking */}
							<div>
								<h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">
									Ranking with these weights
								</h4>
								<div className="rounded-md border border-gray-200 overflow-hidden">
									<table className="w-full text-sm">
										<thead className="bg-gray-50">
											<tr>
												<th className="text-left px-3 py-2 font-semibold text-gray-600 w-12">
													Rank
												</th>
												<th className="text-left px-3 py-2 font-semibold text-gray-600">Site</th>
												<th className="text-right px-3 py-2 font-semibold text-gray-600 w-24">
													Score
												</th>
												<th className="text-center px-3 py-2 font-semibold text-gray-600 w-24">
													Change
												</th>
											</tr>
										</thead>
										<tbody>
											{liveRanking.map((row: any) => {
												const baselineRank = baselineRankById[row.siteId]
												const change =
													baselineRank != null ? baselineRank - row.rank : 0
												const isLeader = row.rank === 1
												return (
													<tr
														key={row.siteId}
														className={cn(
															'border-t border-gray-100',
															isLeader && 'bg-[#0080fe]/5',
														)}
													>
														<td className="px-3 py-2">
															<span
																className={cn(
																	'inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-semibold',
																	isLeader
																		? 'bg-[#0080fe] text-white'
																		: 'bg-gray-100 text-gray-600',
																)}
															>
																{row.rank}
															</span>
														</td>
														<td className="px-3 py-2 font-medium text-gray-900 truncate">
															{row.siteName}
														</td>
														<td className="px-3 py-2 text-right tabular-nums font-semibold text-gray-900">
															{row.overallScore != null
																? row.overallScore.toFixed(2)
																: '—'}
														</td>
														<td className="px-3 py-2">
															<div
																className={cn(
																	'inline-flex items-center justify-center gap-1 text-xs font-medium w-full',
																	change > 0
																		? 'text-emerald-700'
																		: change < 0
																			? 'text-red-700'
																			: 'text-gray-400',
																)}
															>
																{change > 0 && <ArrowUp className="w-3 h-3" />}
																{change < 0 && <ArrowDown className="w-3 h-3" />}
																{change === 0 && <Minus className="w-3 h-3" />}
																{change !== 0 && Math.abs(change)}
															</div>
														</td>
													</tr>
												)
											})}
										</tbody>
									</table>
								</div>
							</div>

							{saveError && (
								<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
									<AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
									<span>{saveError}</span>
								</div>
							)}
						</>
					)}
				</div>

				<DialogFooter>
					<Button
						variant="ghost"
						onClick={handleReset}
						disabled={!dirty || saving}
						className="gap-1.5"
					>
						<RotateCcw className="w-4 h-4" />
						Reset
					</Button>
					<Button variant="ghost" onClick={onClose} disabled={saving}>
						Close
					</Button>
					<Button
						variant="primary"
						onClick={handleSave}
						disabled={!dirty || saving || !hasScores}
						className="gap-1.5"
					>
						{saving ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Saving…
							</>
						) : (
							<>
								<Save className="w-4 h-4" />
								Save these weights
							</>
						)}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}

export default SensitivityAnalysis
